import React from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, UseFormReturn} from 'react-hook-form';
import ToggleSwitch from '../../../../../incase-domain/components/toggleSwitch/ToggleSwitch';
import DebouncedNumberInput from '../../../../../components/slides/highlighted/common/debounced/DebounceNumberInput';
import TooltipWrapper from '../../../../components/common/TooltipWrapper';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import {TOOLTIP_DELAY} from '../../../../../constants';
import {
    DEFAULT_LEADERBOARD_SHOW_PERSONAL_RANK,
    DEFAULT_LEADERBOARD_SIZE,
    DEFAULT_LEADERBOARD_VISIBILITY,
    LeaderboardVisibility,
} from '../../../../../incase-domain/rooms/room.editor';

interface EditRoomGamificationTabProps {
    useForm: UseFormReturn;
}

function EditRoomGamificationTab({useForm}: EditRoomGamificationTabProps): JSX.Element {
    const {t} = useTranslation();
    const {watch, setValue, control} = useForm;

    const showLeaderboard = !!watch('showLeaderboard');

    const visibility = (watch('leaderboardVisibility') as LeaderboardVisibility | undefined) ?? DEFAULT_LEADERBOARD_VISIBILITY;
    const leaderboardSize = (watch('leaderboardSize') as number | undefined) ?? DEFAULT_LEADERBOARD_SIZE;
    const showPersonalRank = (watch('leaderboardShowPersonalRank') as boolean | undefined) ?? DEFAULT_LEADERBOARD_SHOW_PERSONAL_RANK;
    const isTopN = visibility === LeaderboardVisibility.TOP_N;

    const visibilityButtons = [
        {
            value: LeaderboardVisibility.TOP_N,
            label: t('presEditPage.header.gamificationModal.leaderboardVisibilityTopN'),
            dataCy: 'leaderboard-visibility-topN',
        },
        {
            value: LeaderboardVisibility.ALL,
            label: t('presEditPage.header.gamificationModal.leaderboardVisibilityAll'),
            dataCy: 'leaderboard-visibility-all',
        },
    ];

    return (
        <div>
            <div className="c-editRoomModal__content_header">{t('presEditPage.header.gamificationModal.title')}</div>

            <TooltipWrapper
                delayShow={TOOLTIP_DELAY}
                className="legend__data_content"
                id="show-leaderboard"
                content={t('roomsPage.modals.showLeaderboardTooltip')}
            >
                <div key="toggle-leaderboard" className="u-flex u-gap-2 u-v-center u-h-between u-mt-2">
                    <div>{t('roomsPage.modals.showLeaderboard')}</div>
                    <Controller
                        name="showLeaderboard"
                        control={control}
                        render={({field}) => (
                            <ToggleSwitch id="toggle-leaderboard" isOn={!!field.value} handleToggle={() => field.onChange(!field.value)} />
                        )}
                    />
                </div>
            </TooltipWrapper>

            {showLeaderboard && (
                <div className="u-flex u-flex-col u-gap-3 u-mt-2">
                    <div>
                        <div className="u-flex u-gap-2 u-v-center u-h-between">
                            <div>
                                <strong>{t('presEditPage.header.gamificationModal.leaderboardVisibility')}</strong>
                                <p>{t('presEditPage.header.gamificationModal.leaderboardVisibilityDescription')}</p>
                            </div>
                            <div className="u-flex u-v-center">
                                <ButtonGroup
                                    buttons={visibilityButtons}
                                    value={visibility}
                                    onChange={(value) => setValue('leaderboardVisibility', value)}
                                />
                            </div>
                        </div>
                    </div>

                    {isTopN && (
                        <>
                            <div>
                                <div className="u-flex u-gap-2 u-v-center u-h-between">
                                    <div>
                                        <strong>{t('presEditPage.header.gamificationModal.leaderboardSize')}</strong>
                                        <p>{t('presEditPage.header.gamificationModal.leaderboardSizeDescription')}</p>
                                    </div>
                                    <div className="u-flex u-v-center">
                                        <DebouncedNumberInput
                                            onChange={(value: number) => setValue('leaderboardSize', value)}
                                            min={1}
                                            value={leaderboardSize}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="u-flex u-gap-2 u-v-center u-h-between">
                                    <div>
                                        <strong>{t('presEditPage.header.gamificationModal.leaderboardShowPersonalRank')}</strong>
                                        <p>{t('presEditPage.header.gamificationModal.leaderboardShowPersonalRankDescription')}</p>
                                    </div>
                                    <div className="u-flex u-v-center">
                                        <ToggleSwitch
                                            color="info"
                                            id="leaderboard-show-personal-rank"
                                            isOn={showPersonalRank}
                                            handleToggle={() => setValue('leaderboardShowPersonalRank', !showPersonalRank)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default EditRoomGamificationTab;
